import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { verifyEmail } from "../../store/slices/emailVerification/asyncThunks";
import {
  selectVerifyEmailState,
  clearVerifyState,
} from "../../store/slices/emailVerification/slice";
import { FiCheck, FiX, FiLoader } from "react-icons/fi";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: var(--bg-app);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border-strong);
  border-radius: 12px;
  padding: 2.5rem 2rem;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  text-align: center;
  ${css`
    animation: ${fadeIn} 0.4s ease-out;
  `}

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const IconContainer = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 1.8rem;
  border: 1px solid transparent;

  ${({ $status }) => {
    if ($status === "loading") {
      return css`
        background: rgba(255, 107, 53, 0.16);
        color: var(--brand);
        border-color: rgba(255, 107, 53, 0.35);
        svg {
          animation: ${spin} 1s linear infinite;
        }
      `;
    } else if ($status === "success") {
      return css`
        background: rgba(40, 167, 69, 0.15);
        color: #3ddc74;
        border-color: rgba(40, 167, 69, 0.3);
      `;
    } else {
      return css`
        background: rgba(220, 53, 69, 0.14);
        color: #ff6b6b;
        border-color: rgba(220, 53, 69, 0.3);
      `;
    }
  }}
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: var(--font-display);
  font-size: 1.35rem;
  text-transform: uppercase;
  letter-spacing: -0.01em;
  margin-bottom: 0.75rem;
`;

const Message = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.55;
  margin-bottom: 1.5rem;

  strong {
    color: var(--text-primary);
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 44px;
  padding: 0 1.4rem;
  background: rgba(255, 107, 53, 0.16);
  color: var(--brand);
  border: 1px solid rgba(255, 107, 53, 0.4);
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: rgba(255, 107, 53, 0.26);
    border-color: rgba(255, 107, 53, 0.6);
    transform: translateY(-1px);
  }
`;

const EmailVerificationPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const verifyState = useSelector(selectVerifyEmailState);

  useEffect(() => {
    dispatch(clearVerifyState());

    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");

    if (!token) {
      return;
    }

    dispatch(verifyEmail(token));
  }, [location, dispatch]);

  const handleGoToDashboard = () => {
    navigate("/bend-panel/kontrolna-tabla");
  };

  const getIcon = () => {
    if (verifyState.loading) return <FiLoader />;
    if (verifyState.success) return <FiCheck />;
    return <FiX />;
  };

  const getTitle = () => {
    if (verifyState.loading) return "Verifikacija u toku...";
    if (verifyState.success) return "Verifikacija uspešna!";
    return "Verifikacija neuspešna";
  };

  const getMessage = () => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");

    if (!token) {
      return "Nedostaje verifikacioni token.";
    }

    if (verifyState.loading) {
      return "Molimo sačekajte...";
    }

    if (verifyState.success) {
      return "Vaša email adresa je uspešno verifikovana!";
    }

    if (verifyState.error) {
      return verifyState.error;
    }

    return "Greška pri verifikaciji email adrese.";
  };

  const getStatus = () => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");

    if (!token) return "error";
    if (verifyState.loading) return "loading";
    if (verifyState.success) return "success";
    return "error";
  };

  return (
    <Container>
      <Card>
        <IconContainer $status={getStatus()}>{getIcon()}</IconContainer>

        <Title>{getTitle()}</Title>

        <Message>{getMessage()}</Message>

        {verifyState.user && (
          <Message>
            Dobro došli, <strong>@{verifyState.user.username}</strong>
            {verifyState.user.bandName && ` (${verifyState.user.bandName})`}!
          </Message>
        )}

        {verifyState.success && (
          <Button onClick={handleGoToDashboard}>Idite na Dashboard</Button>
        )}
      </Card>
    </Container>
  );
};

export default EmailVerificationPage;
